// Evidence validity on the Documents page (backend lib/evidenceValidity.ts): what kind of evidence a
// document is, the date it speaks for and who renews it. The periods are the organisation's own
// (operating parameters), so the page reads the catalogue from GET /documents/evidence-types rather
// than holding a copy. A lapsed document stays on file, labelled; nothing here hides it.

import { useEffect, useState } from "react";
import type React from "react";
import { api } from "../api/client";
import { theme, inputStyle } from "../theme";
import { StatusTag } from "./Drawing";
import { fmtDateOnly } from "../lib/dateOnly";

export interface DocumentValidity {
  type: string;
  typeLabel: string;
  evidenceDate: string;
  months: number;
  validUntil: string;
  renewFrom: string;
  standing: "current" | "due" | "lapsed";
}

export interface EvidenceTypeOption {
  type: string;
  label: string;
  months: number;
  dateMeaning: string;
  basis: string;
}

export interface ValiditySuggestion { evidenceType: string | null; evidenceDate: string | null; reasons: string[] }

export interface ValidityDraft {
  evidenceType: string;      // "" = no type
  evidenceDate: string;      // yyyy-mm-dd or ""
  renewalOwnerUserId: string; // "" = nobody
}

export interface Member { id: string; name: string }

/** The catalogue with the organisation's periods, loaded once per page. */
export function useEvidenceTypes(): { types: EvidenceTypeOption[]; noticeDays: number } {
  const [state, setState] = useState<{ types: EvidenceTypeOption[]; noticeDays: number }>({ types: [], noticeDays: 30 });
  useEffect(() => {
    api.get<{ types: EvidenceTypeOption[]; noticeDays: number }>("/documents/evidence-types").then(setState).catch(() => {});
  }, []);
  return state;
}

/** Active members who may be named to renew a document. */
export function useRenewalOwners(): Member[] {
  const [members, setMembers] = useState<Member[]>([]);
  useEffect(() => {
    api.get<{ members: Array<{ id: string; displayName: string | null; email: string; isActive?: boolean }> }>("/organizations/members")
      .then((d) => setMembers(d.members.filter((m) => m.isActive !== false).map((m) => ({ id: m.id, name: m.displayName || m.email }))))
      .catch(() => setMembers([]));
  }, []);
  return members;
}

export async function suggestValidity(name: string): Promise<ValiditySuggestion | null> {
  try {
    return await api.get<ValiditySuggestion>(`/documents/validity-suggestion?name=${encodeURIComponent(name.slice(0, 500))}`);
  } catch {
    return null;
  }
}

/** The document's standing, carried by shape as well as colour. */
export function ValidityChip({ validity }: { validity: DocumentValidity | null | undefined }) {
  if (!validity) return null;
  if (validity.standing === "lapsed") {
    return (
      <StatusTag tone="gap" title={`${validity.typeLabel} dated ${fmtDateOnly(validity.evidenceDate)}; valid for ${validity.months} months. It stays on file but no longer counts as current evidence.`}>
        Out of date since {fmtDateOnly(validity.validUntil)}
      </StatusTag>
    );
  }
  if (validity.standing === "due") {
    return (
      <StatusTag tone="partial" title={`${validity.typeLabel} dated ${fmtDateOnly(validity.evidenceDate)}. Upload the newer one as a new version before it lapses.`}>
        Renew by {fmtDateOnly(validity.validUntil)}
      </StatusTag>
    );
  }
  return (
    <StatusTag tone="met" title={`${validity.typeLabel} dated ${fmtDateOnly(validity.evidenceDate)}; valid for ${validity.months} months.`}>
      Valid until {fmtDateOnly(validity.validUntil)}
    </StatusTag>
  );
}

const label: React.CSSProperties = { display: "block", fontSize: 11, fontWeight: 600, color: theme.color.muted, marginBottom: 4 };

/**
 * Type, evidence date and renewal owner. `typeLocked` keeps the type as it is (a new version carries it over),
 * `showOwner` false hides the owner (a new version keeps it).
 */
export function ValidityFields({
  value, onChange, types, members, suggestion, typeLocked = false, showOwner = true,
}: {
  value: ValidityDraft;
  onChange: (next: ValidityDraft) => void;
  types: EvidenceTypeOption[];
  members: Member[];
  suggestion?: ValiditySuggestion | null;
  typeLocked?: boolean;
  showOwner?: boolean;
}) {
  const chosen = types.find((t) => t.type === value.evidenceType) ?? null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
        <label>
          <span style={label}>Evidence type</span>
          <select
            value={value.evidenceType}
            disabled={typeLocked}
            onChange={(e) => onChange({ ...value, evidenceType: e.target.value, ...(e.target.value ? {} : { evidenceDate: "", renewalOwnerUserId: "" }) })}
            style={{ ...inputStyle, fontSize: 12 }}
          >
            <option value="">Not a dated record (no validity period)</option>
            {types.map((t) => <option key={t.type} value={t.type}>{t.label} ({t.months} months)</option>)}
          </select>
        </label>
        {value.evidenceType && (
          <label>
            <span style={label}>Evidence date{chosen ? `: ${chosen.dateMeaning}` : ""}</span>
            <input type="date" max={today} value={value.evidenceDate} onChange={(e) => onChange({ ...value, evidenceDate: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} />
          </label>
        )}
        {value.evidenceType && showOwner && (
          <label>
            <span style={label}>Renewed by</span>
            <select value={value.renewalOwnerUserId} onChange={(e) => onChange({ ...value, renewalOwnerUserId: e.target.value })} style={{ ...inputStyle, fontSize: 12 }}>
              <option value="">Nobody named</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {chosen && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>
          Counts as current for {chosen.months} months from the evidence date ({chosen.basis}). Your organisation can change the period under Settings, Operating parameters.
        </div>
      )}
      {suggestion && suggestion.reasons.length > 0 && (
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6 }}>
          Suggested from the file name: {suggestion.reasons.join(" ")} Check it before saving.
        </div>
      )}
    </div>
  );
}

/** The draft as the API takes it. */
export function validityPayload(d: ValidityDraft) {
  return {
    evidenceType: d.evidenceType || null,
    evidenceDate: d.evidenceType ? d.evidenceDate || null : null,
    renewalOwnerUserId: d.evidenceType ? d.renewalOwnerUserId || null : null,
  };
}
